/**
 * The checkpoint vm in the browser: godev's checkpoint runtime built for wasm (checkpoint/cmd/cpwasm),
 * which the form calls for its pure work, so the form reads and writes a configuration exactly as the
 * Go runtime does. FEATURE-20261029-wasm-checkpoint-interpreter.
 *
 * Every call crosses as JSON text and answers {ok} or {error: {message, path}}; a call's error is
 * thrown as a CheckpointVmError, carrying the form path it is about when there is one.
 *
 * The module is fetched and started once per tab (loadCheckpointVm). bin/fetch-cpwasm.sh puts
 * cpwasm.wasm and the wasm_exec.js of the Go that built it in public/checkpoint/.
 */

/** A call the vm refused: why, and the form path it is about ("setting.probe.Tcp.port"), if any. */
export class CheckpointVmError extends Error {
  constructor(
    message: string,
    readonly path: string = "",
  ) {
    super(message);
    this.name = "CheckpointVmError";
  }
}

/** One message of a validate run, as the Go reader reads it. */
export interface VerdictMessage {
  level: "Error" | "Warn" | "Info";
  text: string;
  path: string;
}

/** A validate run's answer read: its messages, whether the configuration may be saved, whether saving asks to confirm. */
export interface Verdict {
  messages: VerdictMessage[];
  canSave: boolean;
  confirm: boolean;
  errors: number;
}

/** The calls globalThis.checkpointVm offers (checkpoint/cmd/cpwasm). Each answers JSON text. */
interface RawVm {
  version(): string;
  load(plan: Uint8Array): string;
  plugins(handle: string): string;
  typeTree(handle: string, plugin: string): string;
  defaults(handle: string, plugin: string, configJson: string): string;
  toForm(handle: string, plugin: string, configJson: string, prefix: string): string;
  fromForm(handle: string, plugin: string, formJson: string, prefix: string): string;
  verdict(validateResultJson: string): string;
  call(handle: string, module: string, fn: string, argsJson: string): string;
  run(handle: string, module: string, fn: string, argsJson: string, runId: string): Promise<string>;
  resume(handle: string, stateJson: string, valueJson: string, runId: string): Promise<string>;
  takeTurn(checkpointUid: string, valueJson: string, runId: string): Promise<string>;
  cancel(runId: string): string;
}

/**
 * How a run's endpoint calls leave the page: one continuum endpoint call, answered with the endpoint's
 * response or its error. `to`, when not empty, is the mailbox the call goes to instead of the service's:
 * a dbaccess session's calls go to the process that holds the session (a program's SQL transaction).
 */
export type CallEndpoint = (service: string, endpoint: string, requestJson: string, timeoutMs: number, to?: string) => Promise<{ response: string } | { error: string }>;

/**
 * How a program's spawnOnWorker runs on the worker: the vm has resolved the worker the program named (an
 * alias or a label) and saved the spawn (checkpointUid), and the page asks the worker's job runner to
 * resume it with no job (jobrunner.v1.RunCheckpoint), as the account the alias says (executionUser, ""
 * for the runner's own), answering the function's result as plain JSON, or why it did not finish.
 */
export type RunOnWorker = (workerUid: string, checkpointUid: string, executionUser?: string) => Promise<{ resultJson: string } | { error: string }>;

/** What else the page gives a run: a way to run a spawn on a worker, and the tab's own processrun, which a spawn is saved under. */
export interface PageWorkers {
  runOnWorker?: RunOnWorker;
  processRunUid?: () => string;
}

/** Installs the page's endpoint calls, and its worker runs, for every run (globalThis.checkpointVmHost, which cpwasm reads). */
export function installPageHost(callEndpoint: CallEndpoint, workers: PageWorkers = {}): void {
  (globalThis as { checkpointVmHost?: { callEndpoint: CallEndpoint } & PageWorkers }).checkpointVmHost = { callEndpoint, ...workers };
}

/**
 * A run stopped at a yield. value is what the program yielded; resumeType what a resume must bring
 * in, as the program writes the type ("" for nothing); wakeAt when a sleep ends (the page waits,
 * then resumes with null); state the paused program, text the page holds and hands back unchanged.
 * The state is the same snapshot a worker saves, so a program paused here could resume on one.
 */
export interface Pause {
  kind: "yield" | "checkpoint";
  value: unknown;
  resumeType: string;
  wakeAt?: string;
  state: string;
}

/**
 * How the tab's turn of a stored program ended (mode 2): finished with its result; paused, saved at
 * checkpointUid, at a yield (value, resumeType, wakeAt) or at a call the tab cannot make (needsHost: a
 * worker takes the next turn) or for a grant (needsGrant); failed; or interrupted by Stop, which
 * leaves the program lost until someone resumes it. leg is the turn's processrun the engine opened.
 */
export interface Turn {
  outcome: "finished" | "paused" | "failed" | "interrupted";
  processUid: string;
  leg: string;
  result?: unknown;
  checkpointUid?: string;
  value?: unknown;
  resumeType?: string;
  wakeAt?: string;
  needsHost?: string;
  needsGrant?: string;
  message?: string;
}

/** How a run ended: with its result, or paused at a yield for the page to resume. */
export type RunOutcome = { kind: "done"; value: unknown } | { kind: "paused"; pause: Pause };

let runs = 0;

function answer<T>(text: string): T {
  const a = JSON.parse(text) as { ok?: T; error?: { message?: string; path?: string } };
  if (a.error) throw new CheckpointVmError(a.error.message ?? "the checkpoint vm refused", a.error.path ?? "");
  return a.ok as T;
}

function outcome(text: string): RunOutcome {
  const a = JSON.parse(text) as { paused?: Pause };
  if (a.paused) return { kind: "paused", pause: a.paused };
  return { kind: "done", value: answer<unknown>(text) };
}

/** The checkpoint vm, typed. A handle names a loaded plan: its hash. */
export class CheckpointVm {
  constructor(private readonly raw: RawVm) {}

  version(): { planFormats: [number, number]; build: string } {
    return answer(this.raw.version());
  }

  /** Loads a plan (its bytes, as GetPlan serves them) and answers its handle, the plan's hash. */
  load(plan: Uint8Array): string {
    return answer(this.raw.load(plan));
  }

  /** The plan's named instances as a fileset registers them (pluginDecls). */
  plugins(handle: string): unknown[] {
    return answer(this.raw.plugins(handle));
  }

  /** A plugin's configuration type as the tree its form is built from. */
  typeTree(handle: string, plugin: string): unknown {
    return answer(this.raw.typeTree(handle, plugin));
  }

  /** A stored configuration with what its completion fills in where nothing is stored. */
  defaults(handle: string, plugin: string, config: unknown): unknown {
    return answer(this.raw.defaults(handle, plugin, JSON.stringify(config ?? null)));
  }

  /** A stored configuration laid out as the form's values, under prefix. */
  toForm(handle: string, plugin: string, config: unknown, prefix: string): Record<string, unknown> {
    return answer(this.raw.toForm(handle, plugin, JSON.stringify(config ?? null), prefix));
  }

  /** The configuration from the form's values under prefix; throws, at the field's path, a value that is not of its type. */
  fromForm(handle: string, plugin: string, values: Record<string, unknown>, prefix: string): unknown {
    return answer(this.raw.fromForm(handle, plugin, JSON.stringify(values), prefix));
  }

  /** A validate run's answer, read. */
  verdict(validateResult: unknown): Verdict {
    return answer(this.raw.verdict(JSON.stringify(validateResult ?? null)));
  }

  /** Runs module.fn with args in plain form; refused when the function has an effect the browser does not offer. */
  call(handle: string, module: string, fn: string, args: unknown[]): unknown {
    return answer(this.raw.call(handle, module, fn, JSON.stringify(args)));
  }

  /**
   * Runs module.fn, which may call continuum endpoints (through the page host, installPageHost) and
   * pause at a yield, and nothing else the server does; refused before it runs when it needs more.
   * The page stays live while it runs; stop() ends it at its next turn, an endpoint call in flight
   * included. A paused run comes back as its Pause; resume() continues it.
   */
  run(handle: string, module: string, fn: string, args: unknown[]): { result: Promise<RunOutcome>; stop: () => void } {
    const runId = `run-${++runs}`;
    const result = this.raw.run(handle, module, fn, JSON.stringify(args), runId).then(outcome);
    return { result, stop: () => void this.raw.cancel(runId) };
  }

  /**
   * Continues a paused run with value, what its yield declared (null for a yield that takes nothing,
   * a sleep included), under the plan it ran on. It ends as run does: done, paused again, or refused,
   * a value that is not what the yield declared among the refusals, which leaves the pause resumable.
   */
  /**
   * Takes the next turn of the stored program checkpointUid in this tab, as the logged-in user: the
   * engine opens a leg for the turn, the program continues with value (what its yield declared; null
   * for nothing), and the turn ends as a worker's turn does. Only the program's owner, a worker or an
   * admin may. stop() ends the turn interrupted at its next step.
   */
  takeTurn(checkpointUid: string, value: unknown = null): { result: Promise<Turn>; stop: () => void } {
    const runId = `run-${++runs}`;
    const result = this.raw.takeTurn(checkpointUid, value === null || value === undefined ? "" : JSON.stringify(value), runId).then((text) => answer<Turn>(text));
    return { result, stop: () => void this.raw.cancel(runId) };
  }

  resume(handle: string, pause: Pause, value: unknown): { result: Promise<RunOutcome>; stop: () => void } {
    const runId = `run-${++runs}`;
    const result = this.raw.resume(handle, pause.state, JSON.stringify(value ?? null), runId).then(outcome);
    return { result, stop: () => void this.raw.cancel(runId) };
  }
}

/** Where the module comes from: the bytes of cpwasm.wasm, and a way to define globalThis.Go (wasm_exec.js). */
export interface VmSource {
  wasm(): Promise<BufferSource>;
  defineGo(): Promise<void>;
  /** how a run's endpoint calls leave the page; the served source's go over hermes (page-host.ts) */
  callEndpoint?: CallEndpoint;
  /** how a spawnOnWorker runs on its worker, and the tab's processrun; the served source's go over hermes */
  workers?: PageWorkers;
}

// The tab's processrun as the gateway minted it, once page-host has been loaded (by the first endpoint
// call); "" before that, which makes a spawn refuse rather than save under no processrun.
let tabRun = "";
export function setTabProcessRunUid(uid: string): void {
  tabRun = uid;
}
function tabProcessRunUid(): string {
  return tabRun;
}

/** The module as the page serves it, from public/checkpoint/. */
export function servedSource(base: string = import.meta.env.BASE_URL ?? "/"): VmSource {
  const dir = `${base.replace(/\/$/, "")}/checkpoint`;
  return {
    // a run's endpoint calls go over the tab's hermes connection; loaded on first use, so the vm's
    // module does not pull the client in
    callEndpoint: async (service, endpoint, requestJson, timeoutMs, to) =>
      (await import("./page-host")).hermesEndpoints(service, endpoint, requestJson, timeoutMs, to),
    workers: {
      runOnWorker: async (workerUid, checkpointUid, executionUser) => (await import("./page-host")).hermesRunOnWorker(workerUid, checkpointUid, executionUser),
      processRunUid: () => tabProcessRunUid(),
    },
    async wasm() {
      const r = await fetch(`${dir}/cpwasm.wasm`);
      if (!r.ok) throw new Error(`the checkpoint vm is not served at ${dir}/cpwasm.wasm (${r.status}); run bin/fetch-cpwasm.sh`);
      return r.arrayBuffer();
    },
    defineGo() {
      if ((globalThis as { Go?: unknown }).Go) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = `${dir}/wasm_exec.js`;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`the checkpoint vm's wasm_exec.js is not served at ${s.src}`));
        document.head.appendChild(s);
      });
    },
  };
}

/** The plan format this page's engine serves must be one the module reads; else fail now, naming both. */
export function checkPlanFormat(vm: CheckpointVm, planFormatVersion: number): void {
  const [min, max] = vm.version().planFormats;
  if (planFormatVersion < min || planFormatVersion > max) {
    throw new CheckpointVmError(
      `the server's plans are format ${planFormatVersion}, and this page's checkpoint vm reads formats ${min} to ${max}; reload the page, or deploy a page built with the server's godev`,
    );
  }
}

let started: Promise<CheckpointVm> | undefined;

/** The tab's checkpoint vm, started on first use. */
export function loadCheckpointVm(source: VmSource = servedSource()): Promise<CheckpointVm> {
  if (!started) {
    started = start(source).catch((e) => {
      started = undefined; // a failed start may be retried
      throw e;
    });
  }
  return started;
}

async function start(source: VmSource): Promise<CheckpointVm> {
  await source.defineGo();
  const g = globalThis as unknown as {
    Go: new () => { importObject: WebAssembly.Imports; run(i: WebAssembly.Instance): Promise<void> };
    checkpointVm?: RawVm;
    checkpointVmReady?: () => void;
  };
  const ready = new Promise<void>((resolve) => (g.checkpointVmReady = resolve));
  const go = new g.Go();
  const { instance } = await WebAssembly.instantiate(await source.wasm(), go.importObject);
  void go.run(instance); // runs for as long as the page does
  await ready;
  if (!g.checkpointVm) throw new Error("the checkpoint vm started without binding globalThis.checkpointVm");
  if (source.callEndpoint) installPageHost(source.callEndpoint, source.workers);
  return new CheckpointVm(g.checkpointVm);
}
